import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CheckboxFormInput,
    SelectFormInput,
    TextFormInput,
} from "src/components/form";
import { TrialInstitutionLaboratoryFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import { TrialInstitutionLabSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialInstitutionLaboratoryFinanceReduxFormState } from "src/reducers/reactReduxForms/trialInstitutionLaboratoryFinance";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionLaboratoryFinanceActions from "../../../actions/trialInstitutionLaboratoryFinance";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialInstitutionLaboratoryFinancePage.scss";

interface TrialInstitutionLaboratoryFinancePageProps {
    form: Dtos.TrialInstitutionLaboratoryFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialInstitutionLaboratoryFinanceReduxFormState;
    trialInstitutionLaboratoryFinance: Dtos.TrialInstitutionLaboratoryFinance;
    lookups: Dtos.Lookup[];
    loadingTrialInstitutionLaboratoryFinance: boolean;
    loadTrialInstitutionLaboratoryFinanceSuccess: boolean;
    loadTrialInstitutionLaboratoryFinanceFailure: boolean;
    loadTrialInstitutionLaboratoryFinanceErrorCode: string;
    savingTrialInstitutionLaboratoryFinance: boolean;
    saveTrialInstitutionLaboratoryFinanceSuccess: boolean;
    saveTrialInstitutionLaboratoryFinanceFailure: boolean;
    validatingTrialInstitutionLaboratoryFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialInstitutionLaboratoryFinance: TrialInstitutionLaboratoryFinanceActions.ITrialInstitutionLaboratoryFinanceClearActionCreator;
    loadTrialInstitutionLaboratoryFinance: TrialInstitutionLaboratoryFinanceActions.ITrialInstitutionLaboratoryFinanceLoadByTrialInstitutionLaboratoryIdActionCreator;
    saveTrialInstitutionLaboratoryFinance: TrialInstitutionLaboratoryFinanceActions.ITrialInstitutionLaboratoryFinanceSaveActionCreator;
    validateTrialInstitutionLaboratoryFinance: TrialInstitutionLaboratoryFinanceActions.ITrialInstitutionLaboratoryFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialInstitutionLaboratoryFinance";

const TrialInstitutionLaboratoryFinancePage: FC<
    TrialInstitutionLaboratoryFinancePageProps
> = ({
    form,
    formState,
    formProperties,
    trialInstitutionLaboratoryFinance,
    lookups,
    loadingTrialInstitutionLaboratoryFinance,
    loadTrialInstitutionLaboratoryFinanceFailure,
    loadTrialInstitutionLaboratoryFinanceErrorCode,
    savingTrialInstitutionLaboratoryFinance,
    saveTrialInstitutionLaboratoryFinanceSuccess,
    saveTrialInstitutionLaboratoryFinanceFailure,
    validatingTrialInstitutionLaboratoryFinance,
    validationFailures,
    permissions,
    clearTrialInstitutionLaboratoryFinance,
    loadTrialInstitutionLaboratoryFinance,
    saveTrialInstitutionLaboratoryFinance,
    validateTrialInstitutionLaboratoryFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { trialInstitutionLaboratoryId } = useParams<{
        trialInstitutionLaboratoryId: string;
    }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);
    const trialInstitutionLaboratoryIdNumber = parseInt(
        trialInstitutionLaboratoryId,
        10
    );

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Institution Laboratory Finance
    useEffect(() => {
        if (
            trialInstitutionLaboratoryIdNumber &&
            !trialInstitutionLaboratoryFinance
        ) {
            setBreadcrumbs(
                TrialInstitutionLaboratoryFinanceBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber
                )
            );

            loadTrialInstitutionLaboratoryFinance(
                trialInstitutionLaboratoryIdNumber
            );
        } else if (trialInstitutionLaboratoryFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionLaboratoryIdNumber,
        trialInstitutionLaboratoryFinance,
        permissions,
        formInitalised,
        setBreadcrumbs,
        loadTrialInstitutionLaboratoryFinance,
        TrialInstitutionLaboratoryFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionLaboratoryFinanceFailure) {
            navigate(
                `/error/${loadTrialInstitutionLaboratoryFinanceErrorCode}`
            );
        }
    }, [
        loadTrialInstitutionLaboratoryFinanceFailure,
        loadTrialInstitutionLaboratoryFinanceErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialInstitutionLaboratoryIdNumber &&
            trialInstitutionLaboratoryFinance?.trialInstitutionLaboratory
        ) {
            setDrawerItems(
                TrialInstitutionLabSubMenu(
                    trialInstitutionLaboratoryFinance?.trialInstitutionLaboratory,
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionLaboratoryFinanceBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionLaboratoryIdNumber,
                    trialInstitutionLaboratoryFinance.trialInstitutionLaboratory
                        ?.trialInstitution?.trial?.displayName,
                    trialInstitutionLaboratoryFinance.trialInstitutionLaboratory
                        ?.trialInstitution?.institution?.name,
                    trialInstitutionLaboratoryFinance.trialInstitutionLaboratory
                        ?.laboratory?.name
                )
            );
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitutionLaboratoryIdNumber,
        trialInstitutionLaboratoryFinance,
        setBreadcrumbs,
        setDrawerItems,
        TrialInstitutionLaboratoryFinanceBreadcrumbs,
        TrialInstitutionLabSubMenu,
    ]);

    // Reload trial institution laboratory finance after saving
    useEffect(() => {
        if (
            !savingTrialInstitutionLaboratoryFinance &&
            saveTrialInstitutionLaboratoryFinanceSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial institution laboratory finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs`;
                navigate(url);
            } else if (!trialInstitutionLaboratoryIdNumber) {
                const url = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs/${trialInstitutionLaboratoryFinance.trialInstitutionLaboratory?.id}`;
                navigate(url);
                clearTrialInstitutionLaboratoryFinance();
            }
        }
    }, [
        savingTrialInstitutionLaboratoryFinance,
        saveTrialInstitutionLaboratoryFinanceSuccess,
        trialInstitutionLaboratoryIdNumber,
        trialIdNumber,
        trialInstitutionIdNumber,
        navigate,
        saveAndReturn,
        clearTrialInstitutionLaboratoryFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialInstitutionLaboratoryFinance &&
            saveTrialInstitutionLaboratoryFinanceFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialInstitutionLaboratoryFinance,
        saveTrialInstitutionLaboratoryFinanceSuccess,
    ]);

    // Cleanup effect to clear the trial institution laboratory finance on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionLaboratoryFinance();
            resetForm(reduxFormName);
        };
    }, [clearTrialInstitutionLaboratoryFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialInstitutionLaboratoryFinance =
                {} as Dtos.TrialInstitutionLaboratoryFinance;

            if (!data.trialInstitutionLaboratoryId) {
                changes.trialInstitutionLaboratoryId =
                    trialInstitutionLaboratoryIdNumber;
            }

            await saveTrialInstitutionLaboratoryFinance(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (
        _data: Dtos.TrialInstitutionLaboratoryFinance
    ) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/labs`;
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialInstitutionLaboratoryFinance =
            {} as Dtos.TrialInstitutionLaboratoryFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialInstitutionLaboratoryFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialInstitutionLaboratoryFinance =
                {} as Dtos.TrialInstitutionLaboratoryFinance;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialInstitutionLaboratoryFinance
            ) {
                await validateTrialInstitutionLaboratoryFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialInstitutionLaboratoryIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialInstitutionLaboratoryFinanceUpdate,
                Dtos.Permission.TrialInstitutionLaboratoryFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialInstitutionLaboratoryFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            overrideFundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Institution Email Address",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            fundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overrideFundingEmail,
                                                inputProps: {
                                                    value: !form.overrideFundingEmail
                                                        ? trialInstitutionLaboratoryFinance
                                                              ?.trialInstitutionLaboratory
                                                              ?.laboratory
                                                              ?.laboratoryFinance
                                                              ?.fundingEmailAddress
                                                        : form.fundingEmail,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            accountNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            bsbSwift: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            siteCostCentre: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            overridePaymentDetails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Payment Details",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            abn: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionLaboratoryFinance
                                                              ?.trialInstitutionLaboratory
                                                              ?.laboratory?.abn
                                                        : form.abn,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionLaboratoryFinance
                                                              ?.trialInstitutionLaboratory
                                                              ?.laboratory
                                                              ?.laboratoryFinance
                                                              ?.taxInvoiceName
                                                        : form.taxInvoiceName,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionLaboratoryFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionLaboratoryFinance
                                                              ?.trialInstitutionLaboratory
                                                              ?.laboratory
                                                              ?.laboratoryFinance
                                                              ?.quickbooksTaxCodeId
                                                        : form.quickbooksTaxCodeId,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "InstitutionFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialInstitutionLaboratoryFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialInstitutionLaboratoryFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialInstitutionLaboratoryFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={
                loadingTrialInstitutionLaboratoryFinance ||
                savingTrialInstitutionLaboratoryFinance
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionLaboratoryFinancePageProps> => {
    let trialInstitutionLaboratoryFinance:
        | Dtos.TrialInstitutionLaboratoryFinance
        | undefined = !(
        state.trialInstitutionLaboratoryFinance.formData instanceof Array
    )
        ? state.trialInstitutionLaboratoryFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitutionLaboratoryFinance: trialInstitutionLaboratoryFinance!,
        loadingTrialInstitutionLaboratoryFinance:
            state.trialInstitutionLaboratoryFinance.loadState &&
            state.trialInstitutionLaboratoryFinance.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionLaboratoryFinanceSuccess:
            state.trialInstitutionLaboratoryFinance.loadState &&
            state.trialInstitutionLaboratoryFinance.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionLaboratoryFinanceFailure:
            state.trialInstitutionLaboratoryFinance.loadState &&
            state.trialInstitutionLaboratoryFinance.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionLaboratoryFinanceErrorCode:
            state.trialInstitutionLaboratoryFinance.loadState.errorCode,
        savingTrialInstitutionLaboratoryFinance:
            state.trialInstitutionLaboratoryFinance.saveState &&
            state.trialInstitutionLaboratoryFinance.saveState.status ==
                RequestState.Pending,
        saveTrialInstitutionLaboratoryFinanceSuccess:
            state.trialInstitutionLaboratoryFinance.saveState &&
            state.trialInstitutionLaboratoryFinance.saveState.status ==
                RequestState.Success,
        saveTrialInstitutionLaboratoryFinanceFailure:
            state.trialInstitutionLaboratoryFinance.saveState &&
            state.trialInstitutionLaboratoryFinance.saveState.status ==
                RequestState.Failure,
        validatingTrialInstitutionLaboratoryFinance:
            state.trialInstitutionLaboratoryFinance.validateState &&
            state.trialInstitutionLaboratoryFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialInstitutionLaboratoryFinance.lookups,
        form: state.reduxForms.trialInstitutionLaboratoryFinance,
        formState: state.trialInstitutionLaboratoryFinance.formState,
        formProperties: state.trialInstitutionLaboratoryFinance.formProperties,
        reduxFormState:
            state.reduxForms.formState.trialInstitutionLaboratoryFinance,
        validationFailures: trialInstitutionLaboratoryFinance
            ? state.trialInstitutionLaboratoryFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionLaboratoryFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialInstitutionLaboratoryFinance: bindActionCreators(
            TrialInstitutionLaboratoryFinanceActions.LoadTrialInstitutionLaboratoryFinanceByTrialInstitutionLaboratoryId,
            dispatch
        ),
        clearTrialInstitutionLaboratoryFinance: bindActionCreators(
            TrialInstitutionLaboratoryFinanceActions.Clear,
            dispatch
        ),
        saveTrialInstitutionLaboratoryFinance: bindActionCreators(
            TrialInstitutionLaboratoryFinanceActions.SaveTrialInstitutionLaboratoryFinance,
            dispatch
        ),
        validateTrialInstitutionLaboratoryFinance: bindActionCreators(
            TrialInstitutionLaboratoryFinanceActions.ValidateTrialInstitutionLaboratoryFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionLaboratoryFinancePage);
